/**
 * Classic mini-basket fragment refresh bridge.
 *
 * WooCommerce / Impreza listen for `added_to_cart` and call `$button.closest(...)`.
 * Never pass a null button — that throws "Cannot read properties of null (reading 'closest')".
 * Impreza’s header notice reads `data-product_name` from that button (or a nearby card title).
 *
 * @param {object|null} cart Store API cart payload, if any.
 * @param {Element|null} trigger Clicked control to expose as the Woo `$button`.
 */
export function refreshCartFragments(cart, trigger = null) {
	document.dispatchEvent(
		new CustomEvent('wcQuickBuy:cartUpdated', {
			detail: { cart },
			bubbles: true,
		})
	);

	if (!window.jQuery) {
		return;
	}

	const $ = window.jQuery;
	const $button = trigger ? $(trigger) : $();
	decorateAddButton($, $button, trigger, cart);

	const productName = ($button.attr('data-product_name') || '').trim();
	const fragments = {
		...((cart && (cart.fragments || cart.cart_fragments)) || {}),
		...noticeFragments(productName),
	};
	const cartHash = (cart && (cart.cart_hash || cart.cartHash)) || '';

	$(document.body).trigger('wc_fragment_refresh');
	$(document.body).trigger('added_to_cart', [fragments, cartHash, $button]);
	stripViewBasketLink($, $button);
	applyProductNameToNotification(productName);
}

/**
 * Copy product id/name onto the trigger so Impreza’s mini-cart notice can read them.
 *
 * @param {JQueryStatic} $
 * @param {JQuery} $button
 * @param {Element|null} trigger
 * @param {object|null} cart
 */
function decorateAddButton($, $button, trigger, cart) {
	if (!$button || !$button.length) {
		return;
	}

	const root = closestRoot(trigger);
	const productId =
		$button.attr('data-product_id') ||
		(trigger && trigger.getAttribute && trigger.getAttribute('data-product_id')) ||
		(root && (root.getAttribute('data-product-id') || root.getAttribute('data-product_id'))) ||
		'';

	let productName =
		$button.attr('data-product_name') ||
		(trigger && trigger.getAttribute && trigger.getAttribute('data-product_name')) ||
		(root && (root.getAttribute('data-product-name') || root.getAttribute('data-product_name'))) ||
		'';

	if (!productName) {
		productName = nameFromNearbyTitle(root || trigger);
	}

	if (!productName) {
		productName = nameFromCart(cart);
	}

	if (productId) {
		$button.attr('data-product_id', productId);
		$button.data('product_id', productId);
	}

	if (productName) {
		$button.attr('data-product_name', productName);
		$button.data('product_name', productName);
	}

	if (!$button.attr('data-quantity')) {
		$button.attr('data-quantity', '1');
		$button.data('quantity', 1);
	}
}

/**
 * @param {Element|null} trigger
 * @return {Element|null}
 */
function closestRoot(trigger) {
	if (!trigger || typeof trigger.closest !== 'function') {
		return null;
	}

	return (
		trigger.closest('.wcqb') ||
		trigger.closest('tr.wishlist_item') ||
		trigger.closest('.w-grid-item') ||
		trigger.closest('.product') ||
		null
	);
}

/**
 * @param {Element|null} root
 * @return {string}
 */
function nameFromNearbyTitle(root) {
	if (!root || typeof root.querySelector !== 'function') {
		return '';
	}

	const scope = root.closest ? root.closest('tr.wishlist_item') || root : root;
	const title = scope.querySelector(
		'.woocommerce-loop-product__title, .post_title a, .post_title, .product-name a, .product-name, .wcqb-modal__title, [data-wcqb-title]'
	);

	return title ? String(title.textContent || '').trim() : '';
}

/**
 * @param {object|null} cart
 * @return {string}
 */
function nameFromCart(cart) {
	const items = cart && (cart.items || cart.line_items);
	if (!Array.isArray(items) || !items.length) {
		return '';
	}

	const last = items[items.length - 1];
	return String((last && (last.name || last.title)) || '').trim();
}

/**
 * WooCommerce-style notice fragments so themes that read them get the product name.
 *
 * @param {string} name
 * @return {object}
 */
function noticeFragments(name) {
	if (!name) {
		return {};
	}

	const safe = escapeHtml(name);
	const html = `<div class="woocommerce-message" role="alert">“${safe}” has been added to your basket.</div>`;

	return {
		'div.woocommerce-notices-wrapper': `<div class="woocommerce-notices-wrapper">${html}</div>`,
		'.woocommerce-notices-wrapper': `<div class="woocommerce-notices-wrapper">${html}</div>`,
	};
}

/**
 * Impreza often shows a generic “Added to basket” when it cannot find a card title.
 * If the notice is already visible without the product name, put the name in.
 *
 * @param {string} name
 */
function applyProductNameToNotification(name) {
	if (!name) {
		return;
	}

	const patch = () => {
		document
			.querySelectorAll(
				'.w-cart-notification, .w-cart-notification-title, .w-cart-popup, .w-cart-popup-text, .us-cart-notification, [class*="cart-notification"]'
			)
			.forEach((el) => {
				const text = String(el.textContent || '').trim();
				if (!text || text.includes(name)) {
					return;
				}

				if (
					/^added to (your )?(cart|basket)/i.test(text) ||
					/^(has been )?added to (your )?(cart|basket)/i.test(text)
				) {
					el.textContent = `${name} ${text.charAt(0).toLowerCase()}${text.slice(1)}`;
				}
			});
	};

	patch();
	window.setTimeout(patch, 50);
	window.setTimeout(patch, 250);
}

/**
 * @param {string} value
 * @return {string}
 */
function escapeHtml(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

/**
 * WooCommerce inserts "View basket" / "View cart" beside the clicked control.
 * Quick Add already confirms on the button — do not keep that extra link.
 *
 * @param {JQueryStatic} $
 * @param {JQuery} $button
 */
function stripViewBasketLink($, $button) {
	if (!$button || !$button.length) {
		return;
	}

	const remove = () => {
		$button.nextAll('a.added_to_cart').remove();
		$button.siblings('a.added_to_cart').remove();
		const $card = $button.closest('.wcqb');
		if ($card.length) {
			$card.find('a.added_to_cart').remove();
		}
	};

	remove();
	window.setTimeout(remove, 0);
	$(document.body).one('wc_cart_button_updated', remove);
}
